"use client"
import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { type LegendMarkerPosition } from "@/app/playbook/globe/LegendText"
import {
  getLatestVisibleChartDate,
  getLegendMarkerPositions,
} from "../data/playbookScrollUtils"
import { INITIAL_GRAPH_DATE } from "../data/playbookStateUtils"
import {
  BRANCH_CHOICES_VIEWPORT_FRACTION,
  PLAYBOOK_BRANCH_FLOWCHART_ID,
} from "../branch-node/choicePointAnchor"
import { useGalaxy } from "./useGalaxy"

// The reader is "above the choice point" while the plan-choice flowchart's
// top sits below the choice buttons' post-click resting line
// (BRANCH_CHOICES_VIEWPORT_FRACTION of the viewport — see
// scrollBranchChoicesIntoView). Selecting a plan rests the BUTTONS on that
// line, which leaves the flowchart's top below the viewport top but above
// the line, so this position must count as AT the choice point: measuring
// against the viewport top (rect.top > 0) made the HUD keep its pre-choice
// view — Plan D emphasized — right after the reader picked a plan. False
// when the flowchart isn't in the DOM (e.g. perspective flat documents).
const getIsAboveChoicePoint = () => {
  const flowchart = document.getElementById(PLAYBOOK_BRANCH_FLOWCHART_ID)
  if (!flowchart) return false
  return flowchart.getBoundingClientRect().top
    > window.innerHeight * BRANCH_CHOICES_VIEWPORT_FRACTION
}

// The choose-your-path block (the ChooseYourPathOG diagram + plan buttons)
// counts as on screen while any part of the flowchart wrapper intersects the
// viewport. Drives the HUD graph's all-plans preview fan.
const getIsChoicePointOnScreen = () => {
  const flowchart = document.getElementById(PLAYBOOK_BRANCH_FLOWCHART_ID)
  if (!flowchart) return false
  const rect = flowchart.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}


const areLegendMarkerPositionsEqual = (
  previous: LegendMarkerPosition[],
  next: LegendMarkerPosition[]
) => {
  if (previous.length !== next.length) return false
  return previous.every((previousMarker, index) => {
    const nextMarker = next[index]
    return previousMarker.label === nextMarker.label
      && previousMarker.offset === nextMarker.offset
      && previousMarker.scrollTopPx === nextMarker.scrollTopPx
  })
}

export const usePlaybookScrollState = (
  contentSectionRef: RefObject<HTMLDivElement | null>,
  isEpilogueExpanded: boolean,
  isPlanASelected: boolean,
  // Branch stubs (B/C/D) carry 0.1-year side-panel phase data but only a
  // handful of dateMarkers — interpolate the date between markers on that
  // grid so the HUD sweeps every phase.
  isBranchPlanSelected: boolean,
  // Plan A / pre-choice: stepwise markers, except through the 2029-2030 deal
  // window where the date interpolates on the trimester grid (matching the
  // power block's Jan/May/Sep columns and the month-carrying year pill).
  // False for Plan S, which stays fully stepwise on its annual phases.
  isPlanAOrPreChoiceView: boolean
) => {
  const [graphDate, setGraphDate] = useState(() => INITIAL_GRAPH_DATE)
  const [isAboveChoicePoint, setIsAboveChoicePoint] = useState(false)
  const [isChoicePointOnScreen, setIsChoicePointOnScreen] = useState(false)
  const [legendMarkerPositions, setLegendMarkerPositions] = useState<LegendMarkerPosition[]>([])
  const scrollFrameIdRef = useRef<number | null>(null)
  const {
    showGalaxy,
    isEpilogueScrolledTo,
    galaxyProgress,
    updateGalaxyProgressFromScroll,
    galaxyFadeInDurationMs,
  } = useGalaxy(contentSectionRef, isEpilogueExpanded && isPlanASelected)

  const updateGraphDateFromScroll = useCallback(() => {
    const nextDate = getLatestVisibleChartDate({
      interpolatePhaseGrid: isBranchPlanSelected,
      interpolatePhaseGridInDealWindow: isPlanAOrPreChoiceView,
    })
    if (!nextDate) return
    setGraphDate(prev => prev.getTime() === nextDate.getTime() ? prev : nextDate)
  }, [isBranchPlanSelected, isPlanAOrPreChoiceView])

  const updateIsAboveChoicePointFromScroll = useCallback(() => {
    setIsAboveChoicePoint(getIsAboveChoicePoint())
    setIsChoicePointOnScreen(getIsChoicePointOnScreen())
  }, [])

  const updateLegendMarkerPositionsFromScroll = useCallback(() => {
    const nextMarkerPositions = getLegendMarkerPositions(contentSectionRef.current)
    setLegendMarkerPositions(previousMarkerPositions =>
      areLegendMarkerPositionsEqual(previousMarkerPositions, nextMarkerPositions)
        ? previousMarkerPositions
        : nextMarkerPositions
    )
  }, [contentSectionRef])

  const syncStateFromScroll = useCallback(() => {
    updateGraphDateFromScroll()
    updateIsAboveChoicePointFromScroll()
    updateLegendMarkerPositionsFromScroll()
    updateGalaxyProgressFromScroll()
  }, [
    updateGalaxyProgressFromScroll,
    updateGraphDateFromScroll,
    updateIsAboveChoicePointFromScroll,
    updateLegendMarkerPositionsFromScroll,
  ])

  const scheduleSyncStateFromScroll = useCallback(() => {
    if (scrollFrameIdRef.current !== null) return
    scrollFrameIdRef.current = requestAnimationFrame(() => {
      scrollFrameIdRef.current = null
      syncStateFromScroll()
    })
  }, [syncStateFromScroll])

  // Synchronous, pre-paint: the first HUD frame must already carry the
  // measured top-of-page date. This used to be a rAF inside useEffect —
  // both after paint — so the HUD's first frames rendered with the
  // hardcoded INITIAL_GRAPH_DATE (2027-06, two policies in "Current
  // Policy") and then visibly sprang back to the measured 2027-01 (none).
  // The SSR'd content and its data-chart-date markers are in the DOM by
  // this commit, so measuring here is safe; the one-time forced layout is
  // the price of a correct first paint.
  useLayoutEffect(() => {
    syncStateFromScroll()
  }, [syncStateFromScroll])

  useEffect(() => {
    window.addEventListener("scroll", scheduleSyncStateFromScroll, { passive: true })
    window.addEventListener("resize", scheduleSyncStateFromScroll)
    return () => {
      window.removeEventListener("scroll", scheduleSyncStateFromScroll)
      window.removeEventListener("resize", scheduleSyncStateFromScroll)
      if (scrollFrameIdRef.current !== null) {
        cancelAnimationFrame(scrollFrameIdRef.current)
        scrollFrameIdRef.current = null
      }
    }
  }, [scheduleSyncStateFromScroll])

  return {
    galaxyFadeInDurationMs,
    galaxyProgress,
    graphDate,
    isAboveChoicePoint,
    isChoicePointOnScreen,
    isEpilogueScrolledTo,
    legendMarkerPositions,
    showGalaxy,
  }
}
